import {
  type WorkflowColumnConfiguration,
  type WorkflowConfigurationFactory,
} from './workflowConfigs'

/* eslint-disable @typescript-eslint/naming-convention */
export const workflowColumnConfiguration: {
  [wordflowId: string]: {
    [componentType: string]: { [componentName: string]: WorkflowColumnConfiguration }
  }
} = {
  brand_name_in_product_name: {
    attribute: {
      large_image_url: {
        isVisible: true,
        ordering: 1,
      },
      brand_name: {
        isVisible: true,
        ordering: 2,
      },
      product_name: {
        isVisible: false,
      },
      name: {
        isVisible: false,
      },
      product_category_path: {
        isVisible: true,
        ordering: 5,
      },
      locale_id: {
        isVisible: true,
        ordering: 6,
      },
    },
    chat_gpt: {
      product_name: {
        componentTypeLayers: ['attribute'],
        showComponentTypeLabels: true,
        isFlaggable: true,
        isSuggestible: true,
        isVisible: true,
        ordering: 3,
      },
      name: {
        componentTypeLayers: ['attribute'],
        showComponentTypeLabels: true,
        isFlaggable: true,
        isSuggestible: true,
        isVisible: true,
        ordering: 4,
      },
    },
  },
  size_columns: {
    attribute: {
      large_image_url: {
        isVisible: true,
        ordering: 1,
      },
      size: {
        isVisible: false,
      },
      size_value: {
        isVisible: false,
      },
      size_uom: {
        isVisible: false,
      },
      unit_count: {
        isVisible: true,
        isFlaggable: true,
        isSuggestible: true,
        ordering: 5,
      },
      unit_uom: {
        isVisible: false,
      },
      unit_value: {
        isVisible: false,
      },
      brand_name: {
        isVisible: true,
        ordering: 6,
      },
      product_name: {
        isVisible: true,
        ordering: 7,
      },
      product_category_path: {
        isVisible: true,
        ordering: 8,
      },
      locale_id: {
        isVisible: true,
        ordering: 9,
      },
    },
    chat_gpt: {
      size: {
        componentTypeLayers: ['attribute'],
        showComponentTypeLabels: true,
        isFlaggable: true,
        isSuggestible: true,
        isVisible: true,
        ordering: 2,
      },
      size_value: {
        componentTypeLayers: ['attribute'],
        showComponentTypeLabels: true,
        isFlaggable: true,
        isSuggestible: true,
        isVisible: false,
        ordering: 3,
      },
      size_uom: {
        componentTypeLayers: ['attribute'],
        showComponentTypeLabels: true,
        isFlaggable: true,
        isSuggestible: true,
        isVisible: false,
        ordering: 4,
      },
    },
  },
  image_attributes_extraction: {
    attribute: {
      retailer_name: {
        isVisible: true,
        ordering: 1,
      },
      large_image_url: {
        isVisible: true,
        ordering: 6,
      },
      image_url_angle_top: {
        isVisible: true,
        ordering: 7,
      },
      image_url_angle_left: {
        isVisible: true,
        ordering: 8,
      },
      image_url_angle_right: {
        isVisible: true,
        ordering: 9,
      },
      size: {
        isVisible: false,
      },
      size_value: {
        isVisible: false,
      },
      size_uom: {
        isVisible: false,
      },
      unit_count: {
        isVisible: false,
      },
      unit_uom: {
        isVisible: false,
      },
      unit_value: {
        isVisible: false,
      },
      product_name: {
        isVisible: true,
        ordering: 10,
      },
      brand_name: {
        isVisible: true,
        ordering: 11,
      },
      product_category_path: {
        isVisible: false,
      },
      locale_id: {
        isVisible: true,
        ordering: 11,
      },
      retailer_id: {
        isVisible: true,
        ordering: 2,
      },
    },
    chat_gpt: {
      size: {
        componentTypeLayers: ['attribute'],
        showComponentTypeLabels: true,
        isFlaggable: true,
        isSuggestible: true,
        isVisible: true,
        ordering: 2,
      },
      size_value: {
        componentTypeLayers: ['attribute'],
        showComponentTypeLabels: true,
        isFlaggable: true,
        isSuggestible: true,
        isVisible: false,
        ordering: 3,
      },
      size_uom: {
        componentTypeLayers: ['attribute'],
        showComponentTypeLabels: true,
        isFlaggable: true,
        isSuggestible: true,
        isVisible: false,
        ordering: 4,
      },
      unit_count: {
        componentTypeLayers: ['attribute'],
        showComponentTypeLabels: true,
        isFlaggable: true,
        isSuggestible: true,
        isVisible: true,
        ordering: 5,
      },
    },
  },
}

export const ignorableColumnNamesMap = {
  attribute: {
    classified_type: true,
  },
}

export const automatedFixWorkflowConfigFactory: WorkflowConfigurationFactory = {
  regex: 'automated_fix/.*|human_eval_automated_fix_ingestion/.*|image_attributes_extraction/.*',
  priority: 1,
  build: (workflowId, allowSuggestedValues) => ({
    name: 'automated_fix',
    showCombineButton: false,
    showReviewerAndAuditorDropdown: true,
    showClassifiedType: false,
    showDueDate: false,
    showLocaleId: false,
    sizeFlagChangeCombined: true,
    showSizeModal: true,
    sizeModalConfigs: {
      preFill: false,
      sizeUomPath: {
        type: 'chat_gpt',
        name: 'size_uom',
      },
      sizeValuePath: {
        type: 'chat_gpt',
        name: 'size_value',
      },
      sizePath: {
        type: 'chat_gpt',
        name: 'size',
      },
      unitValuePath: {
        type: 'attribute',
        name: 'unit_value',
      },
      unitUomPath: {
        type: 'attribute',
        name: 'unit_uom',
      },
      unitCountPath: {
        type: 'chat_gpt',
        name: 'unit_count',
      },
    },
    displayProductIdOnly: true,
    displayRetailerProductInformation: true,
    getColumnConfiguration: (componentType, componentName) => {
      const isIgnored =
        ignorableColumnNamesMap[componentType] &&
        ignorableColumnNamesMap[componentType][componentName]
      if (isIgnored)
        return {
          isVisible: false,
        }
      const workFlowIdPaths = workflowId.split('/')
      const workFlowName = workFlowIdPaths && workFlowIdPaths.length > 0 ? workFlowIdPaths[1] : ''

      let workFlowConfig: string
      const attribute_miner_workflows = [
        'attribute_miner_csp_images',
        'attribute_miner_retailer_product_images',
        'attribute_miner_csp_images_multipack_size',
        'attribute_miner_retailer_product_images_multipack_size',
      ]
      if (workFlowName === 'brand_name_in_product_name') {
        workFlowConfig = 'brand_name_in_product_name'
      } else if (attribute_miner_workflows.includes(workFlowName)) {
        workFlowConfig = 'image_attributes_extraction'
      } else {
        workFlowConfig = 'size_columns'
      }

      const workflowColumnConfigurations = workflowColumnConfiguration[workFlowConfig]
      if (!workflowColumnConfigurations) return null
      const columnTypeConfiguration = workflowColumnConfigurations[componentType]
      if (columnTypeConfiguration && columnTypeConfiguration[componentName])
        return columnTypeConfiguration[componentName]
      return {
        isVisible: false,
      }
    },
  }),
}
